import React from "react";
import { useNavigate } from "react-router-dom";
import { CustomModal } from "@ippon/shared-components";
import RepairCreatePage from "@/pages/Repairs/New";
import type { AreaType } from "@ippon/shared-types";

interface Props {
  open: boolean;
  onClose: () => void;
  propertyId: string;
  /** 作成成功時のコールバック（一覧再取得などに利用） */
  onCreated?: (createdId: string) => void;
  /** 場所区分の初期値 (private / common / facility / land / other) */
  initialScope?: AreaType;
  /** 名称 (area = unit_id) の初期値。専有部/共用部詳細のように対象 Unit が確定している時に渡す */
  initialAreaId?: string;
}

/**
 * 修繕工事 新規登録モーダル。
 *
 * NOTE:
 *   - 旧実装は `PreviewDrawer` (Notion 風プレビュー) だったが、
 *     新規登録 UI 統一方針により `CustomModal` の `registerModal` モードに移行。
 *   - 既存ファイル名 `RepairCreateDrawer` は呼び出し元の互換のため維持。
 *   - フォーム本体は `RepairCreatePage`（embedded=true）をそのまま再利用。
 *     送信ボタン／キャンセルボタンは `FixedActionBar`（embedded モード）が
 *     モーダル下端に表示される。
 *   - 登録成功時は `/repairs/{id}` 詳細ページへ遷移する
 *     (非埋込みフローと同じく toast を state 経由で渡す)。
 */
const RepairCreateDrawer: React.FC<Props> = ({
  open,
  onClose,
  propertyId,
  onCreated,
  initialScope,
  initialAreaId,
}) => {
  const navigate = useNavigate();
  return (
    <CustomModal
      openModal={open}
      handleCloseModal={onClose}
      title="工事登録"
      modalType="registerModal"
      addTitleBorder
    >
      {open && (
        <RepairCreatePage
          overridePropertyId={propertyId}
          embedded
          initialScope={initialScope}
          initialAreaId={initialAreaId}
          onCreated={(createdId) => {
            onCreated?.(createdId);
            onClose();
            // onClose() による MUI Modal の scroll lock 解除を待ってから遷移する。
            // 同期で navigate するとアンマウント時に body の overflow:hidden が残り、
            // 遷移先ページがスクロール不能になる。
            setTimeout(() => {
              navigate(`/repairs/${createdId}`, {
                state: {
                  propertyId,
                  fromSource: "repairs",
                  showToast: {
                    message: "工事を登録しました",
                    type: "success",
                  },
                },
              });
            }, 0);
          }}
          onCancel={onClose}
        />
      )}
    </CustomModal>
  );
};

export default RepairCreateDrawer;
